import { Injectable } from "@angular/core";
import { CanLoad, Route, UrlSegment, Router, CanActivate, NavigationEnd } from "@angular/router";
import { Observable, from, of } from "rxjs";
import { take, switchMap, catchError, filter, timeout } from "rxjs/operators";
import { FirebaseAuthService } from "../auth/firebase-auth.service";
import { CurrentPlatformService } from "../services/current-platform.service";
import { loginQueryParams, LoginContinuationService } from "../auth/login-continuation.service";
import { CoreService } from "../core/core.service";
import { OfflineModeService } from "../services/offline-mode.service";
import { Preferences } from "@capacitor/preferences";

@Injectable({
  providedIn: "root",
})
export class AuthGuard implements CanLoad, CanActivate {
  private static readonly MAX_NAVIGATION_RETRIES = 3;
  private static readonly NAVIGATION_RETRY_KEY = "auth_navigation_retries";
  private static readonly PUBLIC_ROUTES = ["/welcome", "/slides", "/error"];

  constructor(
    private router: Router,
    private ngFireAuth: FirebaseAuthService,
    private currentPlatformService: CurrentPlatformService,
    private continuation: LoginContinuationService,
    private core: CoreService,
    private offlineModeService: OfflineModeService,
  ) {
    // Listen to navigation events and reset retry count when successfully navigating to public routes
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      if (this.isPublicRoute(event.url)) {
        this.resetNavigationRetries();
      }
    });
  }

  canActivate(): Observable<boolean> {
    return this.checkAuth();
  }

  canLoad(route: Route, segments: UrlSegment[]): Observable<boolean> {
    return this.checkAuth();
  }

  private checkAuth(): Observable<boolean> {
    // If offline, check for cached authentication data
    if (!this.offlineModeService.isOnlineValue) {
      return this.checkOfflineAuth();
    }

    // `from(currentUser)` rather than `authState.pipe(take(1))`.
    //
    // authState wraps onAuthStateChanged, which is a listener: it only resolves the guard
    // if Firebase actually fires it. Inside a Capacitor WebView the page is served from
    // capacitor://localhost, and there the callback never arrives, so the guard's
    // observable never emits, the navigation never completes, and the router outlet stays
    // empty. That is the whole of the blank white screen on iOS: the app, Ionic, lazy
    // route loading and rendering were all fine, and /welcome (the one route with no
    // guard) always rendered correctly.
    //
    // currentUser is backed by auth.authStateReady(), which is a promise that resolves
    // once Firebase has restored any persisted session, whether or not a listener fires.
    // See docs/qa/findings/05.
    return from(this.ngFireAuth.currentUser).pipe(
      // A guard must never be able to hang the application. If Firebase neither resolves
      // nor rejects, the navigation stays pending forever, the router outlet is never
      // filled, and the user stares at a blank screen with nothing to act on. Inside a
      // Capacitor WebView that is exactly what happened. Falling back to "no user" after
      // five seconds sends the caller down the unauthenticated path, which lands on
      // /welcome: a wrong-but-recoverable screen beats an indefinite blank one.
      timeout({ first: 5000, with: () => of(null) }),
      catchError(() => of(null)),
      take(1),
      switchMap(user => {
        const isLoggedIn = user !== null && user.emailVerified !== false;

        if (!isLoggedIn) {
          return this.handleUnauthenticatedUser();
        }

        this.resetNavigationRetries();
        return of(true);
      }),
    );
  }

  private checkOfflineAuth(): Observable<boolean> {
    return new Observable<boolean>(observer => {
      // Check for cached authentication data from Preferences (Firebase user data)
      Preferences.get({ key: "user" })
        .then(result => {
          const cachedUser = result.value;

          if (cachedUser && cachedUser !== "null") {
            try {
              const userData = JSON.parse(cachedUser);
              // Check if user data exists and email is verified
              if (userData && userData.emailVerified !== false) {
                console.log("Offline auth: Found valid cached user data");
                this.resetNavigationRetries();
                observer.next(true);
                observer.complete();
                return;
              }
            } catch (error) {
              console.error("Error parsing cached user data:", error);
            }
          }

          // Final fallback: Check for other cached authentication data
          Promise.all([
            this.offlineModeService.getFromLocalStorage("selectedHolding"),
            this.offlineModeService.getFromLocalStorage("userProfile"),
          ])
            .then(([cachedHolding, cachedProfile]) => {
              if (cachedHolding || cachedProfile) {
                // User has cached data, allow access
                console.log("Offline auth: Found cached holding/profile data");
                this.resetNavigationRetries();
                observer.next(true);
                observer.complete();
              } else {
                // No cached data, redirect to login
                console.log("Offline auth: No cached authentication data found");
                observer.next(false);
                observer.complete();
                this.handleUnauthenticatedUser();
              }
            })
            .catch(error => {
              console.error("Error checking offline auth:", error);
              observer.next(false);
              observer.complete();
              this.handleUnauthenticatedUser();
            });
        })
        .catch(error => {
          console.error("Error getting user from Preferences:", error);
          observer.next(false);
          observer.complete();
          this.handleUnauthenticatedUser();
        });
    });
  }

  private handleUnauthenticatedUser(): Observable<boolean> {
    const next = this.router.getCurrentNavigation()?.extractedUrl.toString();
    this.continuation.remember(next);
    const queryParams = loginQueryParams({ next });
    return new Observable<boolean>(observer => {
      // Check if we've exceeded navigation retry limit
      this.getNavigationRetryCount()
        .then(retryCount => {
          if (retryCount >= AuthGuard.MAX_NAVIGATION_RETRIES) {
            console.error("Max navigation retries exceeded. Staying on current page to prevent infinite loop.");
            this.router.navigate(["/welcome"], { queryParams });
            observer.next(false);
            observer.complete();
            return;
          }

          // Increment retry count
          this.incrementNavigationRetries()
            .then(() => {
              const isAndroid = this.currentPlatformService.isAndroid();
              const isApple = this.currentPlatformService.isApple();
              const targetRoute = isAndroid || isApple ? "/slides" : "/welcome";

              this.router
                .navigate([targetRoute], { queryParams })
                .then(navigationResult => {
                  if (!navigationResult) {
                    console.error(`Navigation to ${targetRoute} failed`);
                    // Reset retry count even if navigation failed to prevent infinite loops
                    this.resetNavigationRetries();
                  } else {
                    // Reset retry count on successful navigation
                    this.resetNavigationRetries();
                  }
                  observer.next(false);
                  observer.complete();
                })
                .catch(error => {
                  console.error(`Navigation to ${targetRoute} failed with error:`, error);
                  // Reset retry count on navigation error to prevent infinite loops
                  this.resetNavigationRetries();
                  observer.next(false);
                  observer.complete();
                });
            })
            .catch(error => {
              console.error("Error incrementing navigation retries:", error);
              observer.next(false);
              observer.complete();
            });
        })
        .catch(error => {
          console.error("Error getting navigation retry count:", error);
          observer.next(false);
          observer.complete();
        });
    });
  }

  private async getNavigationRetryCount(): Promise<number> {
    try {
      const result = await Preferences.get({ key: AuthGuard.NAVIGATION_RETRY_KEY });
      return result.value ? parseInt(result.value, 10) : 0;
    } catch (error) {
      console.error("Error getting navigation retry count:", error);
      return 0;
    }
  }

  private async incrementNavigationRetries(): Promise<void> {
    try {
      const currentCount = await this.getNavigationRetryCount();
      await Preferences.set({
        key: AuthGuard.NAVIGATION_RETRY_KEY,
        value: (currentCount + 1).toString(),
      });
    } catch (error) {
      console.error("Error incrementing navigation retries:", error);
    }
  }

  private async resetNavigationRetries(): Promise<void> {
    try {
      await Preferences.remove({ key: AuthGuard.NAVIGATION_RETRY_KEY });
    } catch (error) {
      console.error("Error resetting navigation retries:", error);
    }
  }

  private isPublicRoute(url: string): boolean {
    return AuthGuard.PUBLIC_ROUTES.some(route => url.startsWith(route));
  }
}
